import apiService from '@/util/request.js';
import _ from 'lodash';

/** module that contains functions for working with offers
 * @module offerService */

/**
 * fetch available offers by offerSearchKey
 * @param {String} offerSearchKey offerSearchKey
 * @returns {AxiosPromise<any>}
 */
function getOfferList(offerSearchKey) {
  const params = {
    searchKey: offerSearchKey
  };
  return apiService.get('/smp/api/offer2/offers_brief', { params });
}

/**
 * create offer search, which allows searching for available offers for given vehicle/services/equipments
 * @param {Object} rootState vuex root state
 * @param {Object} state state from 'offer' vuex module
 * @returns {AxiosPromise<any>}
 */
function createOfferSearch(state, rootState) {
  let serviceIds = rootState.vehicleServices.selectedServices.map(s => s.id);
  let params = {
    vehicleConfig: {
      constructionTime: rootState.vehicleIdentification.vehicleType.constructionTime ? rootState.vehicleIdentification.vehicleType.constructionTime.valueOf() : null,
      fiCode: rootState.vehicleIdentification.vehicleType.fiCode,
      vin: rootState.vehicleIdentification.vehicleType.vin,
      vehicleTypeEquipmentList: rootState.vehicleEquipments.vehicleEquipmentList.map(v => {
        v.serviceIds = serviceIds;
        return v;
      })
    },
    services: rootState.vehicleServices.selectedServices.map(service => {
      let obj = {
        serviceId: service.id
      };
      if (service.dvnConfigs) {
        _.extend(obj, {
          dvnConfigs: service.dvnConfigs
        })
      }
      return obj;
    })
  };

  if (state.regionSignature) { // assigned during state/offer initialization from default region signature
    // TODO how does legacy webkit distinguish between single/group/portal offersearch
    // if (rootState.region.selectedRegion.signature) { <- this is never set anywhere
    if (rootState?.region?.selectedRegion?.id) {
      _.extend(params, {
        zipAreaId: rootState.region.selectedRegion.id,
        generateEmptyOffer: true
      })
    } else {
      _.extend(params, {
        regionSignature: state.regionSignature,
        generateEmptyOffer: true
      })
    }
  }

  if (rootState.vehicleIdentification.mileage) {
    _.extend(params.vehicleConfig, {
      mileage: rootState.vehicleIdentification.mileage
    })
  }
  // TODO
  // if (rootState.offers.wizardData.maintenancePositions.length > 0) {
  //   _.extend(params, {
  //     maintenancePositions: rootState.offers.wizardData.maintenancePositions
  //   });
  // }
  return createOfferSearchApi(params);
}

/**
 * create offer search
 * - api function
 * @param {String} params.vehicleConfig.constructionTime vehicle construction time
 * @param {String} params.vehicleConfig.fiCode vehicle fiCode
 * @param {String} params.vehicleConfig.vin vehicle vin
 * @param {String} params.vehicleConfig.vehicleTypeEquipmentList vehicle equipments
 * @param {Object[]} params.services array of vehicle service objects with serviceId and dvnConfigs properties
 * @returns {AxiosPromise<any>}
 */
function createOfferSearchApi(params) {
  return apiService.post('/smp/api/location/offers/search', params, { customError: true })
}

/**
 * create simple offer search
 * @param {String[]} params.locationIds id of location
 * @param {String[]} params.serviceIds service ids
 * @returns {AxiosPromise<any>}
 */
function createSimpleOfferSearch(params) {
  return apiService.post('/smp/api/location/offers/search-simple', params, { customError: true })
}

/**
 * create simple offer search
 * @param {String} offerKey offer key
 * @param {String[]} params.selectedServiceIds service ids
 * @returns {AxiosPromise<any>}
 */
function getOfferDetails(offerKey, params) {
  params = params || {};
  return apiService.get(`/smp/api/offer2/offers_brief/${offerKey}/detail`, { params });
}

/**
 * fetch offer searches by offer search key
 * @param {String} offerSearchKey offer search key
 * @returns {AxiosPromise<any>}
 */
function getOfferSearch(offerSearchKey) {
  return apiService.get(`/smp/api/offer2/offersearches/${offerSearchKey}`);
}

/**
 * fetch part qualities
 * @returns {AxiosPromise<any>}
 */
function getPartQualityTypeList() {
  return apiService.get('/smp/api/partqualities/localized');
}

/**
 * fetch list of guilds for country/language
 * @returns {AxiosPromise<any>}
 */
function getGuildList(id) {
  if (!id) id = '0';
  return apiService.get(`/smp/api/locations/guildmembership/${id}/children`);
}

export default {
  getOfferList,
  createOfferSearch,
  getPartQualityTypeList,
  getGuildList,
  getOfferSearch,
  getOfferDetails,
  createOfferSearchApi,
  createSimpleOfferSearch
}
